import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ISupportRepository,
  SupportThread,
  SupportMessage,
  SupportAttachment,
  SupportAttachmentInput,
  SupportThreadStatus,
  SupportThreadType,
  SupportSenderRole,
  TicketStatus,
  TicketPriority,
} from '@erp/data-access/interfaces';

/**
 * Tenant-side support / helpdesk — plain table access scoped by RLS
 * (tenant_id = current_user_tenant_id()). A user sees their OWN conversations
 * (opened_by = auth.uid()). The AI reply comes from the ai-generate Edge
 * Function (SupabaseAiRepository); status transitions are decided by
 * libs/domain/support and passed in here. Attachments live in the private
 * `support-attachments` bucket — uploaded via IStorageService, recorded here.
 */
export class SupabaseSupportRepository implements ISupportRepository {
  constructor(private supabase: SupabaseClient) {}

  async listMyThreads(): Promise<SupportThread[]> {
    const { data: auth } = await this.supabase.auth.getUser();
    const uid = auth?.user?.id;
    let q = this.supabase
      .from('support_threads')
      .select('*')
      .order('last_message_at', { ascending: false });
    if (uid) q = q.eq('opened_by', uid);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(mapThread);
  }

  async createThread(
    type: SupportThreadType,
    subject?: string | null,
  ): Promise<SupportThread> {
    // tenant_id / opened_by fall back to DB defaults (current_user_tenant_id(),
    // auth.uid()), so the client doesn't need to know its own ids.
    const { data, error } = await this.supabase
      .from('support_threads')
      .insert({ status: 'ai', type, subject: subject ?? null })
      .select('*')
      .single();
    if (error) throw error;
    return mapThread(data);
  }

  async getThread(threadId: string): Promise<SupportThread | null> {
    const { data, error } = await this.supabase
      .from('support_threads')
      .select('*')
      .eq('id', threadId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapThread(data) : null;
  }

  async listMessages(threadId: string): Promise<SupportMessage[]> {
    const { data, error } = await this.supabase
      .from('support_messages')
      .select('*, attachments:support_attachments(*)')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapMessage);
  }

  async sendMessage(
    threadId: string,
    body: string,
    role: Exclude<SupportSenderRole, 'admin'>,
    attachments?: SupportAttachmentInput[],
  ): Promise<SupportMessage> {
    const trimmed = body.trim();
    const { data: msg, error } = await this.supabase
      .from('support_messages')
      .insert({
        thread_id: threadId,
        sender_role: role,
        sender_user_id: role === 'ai' ? null : undefined,
        body: trimmed,
      })
      .select('*')
      .single();
    if (error) throw error;

    let savedAttachments: SupportAttachment[] = [];
    if (attachments && attachments.length > 0) {
      const rows = attachments.map((a) => ({
        message_id: msg.id,
        thread_id: threadId,
        storage_path: a.storagePath,
        file_name: a.fileName,
        mime_type: a.mimeType,
        size_bytes: a.sizeBytes,
        width: a.width ?? null,
        height: a.height ?? null,
      }));
      const { data: att, error: attErr } = await this.supabase
        .from('support_attachments')
        .insert(rows)
        .select('*');
      if (attErr) throw attErr;
      savedAttachments = (att ?? []).map(mapAttachment);
    }

    // Refresh the thread preview; bump the admin's unread on user turns.
    const preview = trimmed
      ? trimmed.slice(0, 140)
      : savedAttachments.length
        ? `📎 ${savedAttachments[0]?.fileName ?? 'pièce jointe'}`
        : '';
    const patch: Record<string, unknown> = {
      last_message_at: msg.created_at,
      last_message_preview: preview,
      updated_at: new Date().toISOString(),
    };
    if (role === 'user') {
      const { data: cur } = await this.supabase
        .from('support_threads')
        .select('unread_for_admin')
        .eq('id', threadId)
        .maybeSingle();
      patch.unread_for_admin = ((cur?.unread_for_admin as number) ?? 0) + 1;
    }
    await this.supabase.from('support_threads').update(patch).eq('id', threadId);

    return { ...mapMessage(msg), attachments: savedAttachments };
  }

  async setThreadStatus(threadId: string, status: SupportThreadStatus): Promise<void> {
    const { error } = await this.supabase
      .from('support_threads')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', threadId);
    if (error) throw error;
  }

  async setThreadMeta(
    threadId: string,
    patch: { type?: SupportThreadType; title?: string },
  ): Promise<void> {
    const update: Record<string, unknown> = {};
    if (patch.type) update.type = patch.type;
    if (patch.title) update.title = patch.title;
    if (Object.keys(update).length === 0) return;
    const { error } = await this.supabase
      .from('support_threads')
      .update(update)
      .eq('id', threadId);
    if (error) throw error;
  }

  async markUserRead(threadId: string): Promise<void> {
    const { error } = await this.supabase
      .from('support_threads')
      .update({ unread_for_user: 0 })
      .eq('id', threadId);
    if (error) throw error;
  }
}

type Row = Record<string, unknown>;

function mapThread(r: Row): SupportThread {
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    openedBy: (r.opened_by as string | null) ?? null,
    status: r.status as SupportThreadStatus,
    type: (r.type as SupportThreadType) ?? 'support',
    title: (r.title as string | null) ?? null,
    ticketStatus: (r.ticket_status as TicketStatus) ?? 'open',
    priority: (r.priority as TicketPriority) ?? 'normal',
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
    assignedAdmin: (r.assigned_admin as string | null) ?? null,
    subject: (r.subject as string | null) ?? null,
    lastMessageAt: String(r.last_message_at),
    lastMessagePreview: (r.last_message_preview as string | null) ?? null,
    unreadForUser: Number(r.unread_for_user ?? 0),
    unreadForAdmin: Number(r.unread_for_admin ?? 0),
    createdAt: String(r.created_at),
    updatedAt: String(r.updated_at),
  };
}

function mapAttachment(r: Row): SupportAttachment {
  return {
    id: String(r.id),
    storagePath: String(r.storage_path),
    fileName: String(r.file_name),
    mimeType: (r.mime_type as string | null) ?? null,
    sizeBytes: r.size_bytes != null ? Number(r.size_bytes) : null,
    width: r.width != null ? Number(r.width) : null,
    height: r.height != null ? Number(r.height) : null,
  };
}

function mapMessage(r: Row): SupportMessage {
  const att = Array.isArray(r.attachments) ? (r.attachments as Row[]) : [];
  return {
    id: String(r.id),
    threadId: String(r.thread_id),
    senderRole: r.sender_role as SupportSenderRole,
    senderUserId: (r.sender_user_id as string | null) ?? null,
    body: String(r.body),
    createdAt: String(r.created_at),
    attachments: att.map(mapAttachment),
  };
}
